import { ref } from "vue";
import router from "@/router";
import {
  NotificationService,
  type NotificationDTO,
} from "@/services/NotificationService";
import { useToast } from "@/services/useToast";

/**
 * App-wide notification state. Module scope, like useToast: the bell and the stream must
 * share one list, not one copy per component instance.
 */
const notifications = ref<NotificationDTO[]>([]);
const unreadCount = ref(0);
const loading = ref(false);
const loadingMore = ref(false);
const hasMore = ref(false);
const connected = ref(false);

const service = new NotificationService();

let eventSource: EventSource | null = null;
/**
 * The browser reconnects on its own after a clean close. It does not on an auth failure,
 * but a flapping backend could still spin, so give up after a few failures in a row and
 * let the next navigation pick things up instead.
 */
let consecutiveFailures = 0;
const MAX_CONSECUTIVE_FAILURES = 5;

/**
 * Set once the user has pressed "load older". The stream re-sends the first page on every
 * reconnect — roughly every four minutes — and that must not silently collapse a list the
 * user deliberately expanded.
 */
let expanded = false;

/** Newest first, by recipient row id, which is the order the server pages in. */
const mergeById = (fresh: NotificationDTO[], existing: NotificationDTO[]) => {
  const byId = new Map<number, NotificationDTO>();
  existing.forEach((n) => byId.set(n.id, n));
  // Fresh rows win: their read state is the authoritative one.
  fresh.forEach((n) => byId.set(n.id, n));
  return [...byId.values()].sort((a, b) => b.id - a.id);
};

const loginPathForCurrentPortal = () =>
  window.location.pathname.startsWith("/admin") ? "/admin/login" : "/login";

export function useNotifications() {
  const { showToast } = useToast();

  /**
   * Fallback for when the stream never opened — a proxy eating text/event-stream, say.
   * Not called on a schedule: the stream is the normal path.
   */
  const fetchAll = async () => {
    loading.value = true;
    try {
      const [page, count] = await Promise.all([
        service.list(),
        service.unreadCount(),
      ]);
      notifications.value = page.notifications;
      hasMore.value = page.hasMore;
      expanded = false;
      unreadCount.value = count;
    } catch (e) {
      console.error("Failed to load notifications", e);
    } finally {
      loading.value = false;
    }
  };

  /**
   * The stream greets every new connection with the current state, so a reconnect catches
   * up without an HTTP call. That matters: HTTP calls refresh the auth cookie, and the
   * stream is deliberately excluded from that so idle sessions still expire.
   */
  const onSnapshot = (event: MessageEvent) => {
    try {
      const snapshot = JSON.parse(event.data);
      if (expanded) {
        // Keep the older pages already on screen; the snapshot only covers the newest one,
        // and its own hasMore says nothing about what lies below what we have loaded.
        notifications.value = mergeById(
          snapshot.notifications,
          notifications.value
        );
      } else {
        notifications.value = snapshot.notifications;
        hasMore.value = snapshot.hasMore;
      }
      unreadCount.value = snapshot.unreadCount;
    } catch (e) {
      console.error("Malformed notification snapshot", e);
    }
  };

  const onNotification = (event: MessageEvent) => {
    try {
      const notification: NotificationDTO = JSON.parse(event.data);
      notifications.value = [notification, ...notifications.value];
      unreadCount.value += 1;
      showToast(notification.title, "info");
    } catch (e) {
      console.error("Malformed notification payload", e);
    }
  };

  const connect = () => {
    if (eventSource) {
      return;
    }
    eventSource = new EventSource(
      `${process.env.VUE_APP_API_URL}/api/v1/notifications/stream`,
      { withCredentials: true }
    );

    eventSource.addEventListener("snapshot", onSnapshot as EventListener);
    eventSource.addEventListener(
      "notification",
      onNotification as EventListener
    );

    eventSource.onopen = () => {
      connected.value = true;
      consecutiveFailures = 0;
    };

    eventSource.onerror = () => {
      connected.value = false;

      // CONNECTING means the server closed the stream cleanly and the browser is already
      // retrying — the normal case, nothing to do.
      if (eventSource?.readyState === EventSource.CONNECTING) {
        consecutiveFailures += 1;
        if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
          disconnect();
        }
        return;
      }

      // CLOSED means a non-200 response: the auth cookie expired while the tab sat idle.
      // The stream deliberately does not refresh that cookie, so this is the idle logout.
      disconnect();
      reset();
      const target = loginPathForCurrentPortal();
      if (window.location.pathname !== target) {
        router.replace(target);
      }
    };
  };

  const disconnect = () => {
    if (eventSource) {
      eventSource.close();
      eventSource = null;
    }
    connected.value = false;
  };

  const markAsRead = async (id: number) => {
    const notification = notifications.value.find((n) => n.id === id);
    if (!notification || notification.readAt) {
      return;
    }
    // Optimistic: the badge should drop the moment the row is clicked, and a failed PATCH
    // is corrected by the next fetchAll.
    notification.readAt = new Date().toISOString();
    unreadCount.value = Math.max(0, unreadCount.value - 1);
    try {
      await service.markAsRead(id);
    } catch (e) {
      console.error("Failed to mark notification as read", e);
    }
  };

  const markAllAsRead = async () => {
    const readAt = new Date().toISOString();
    notifications.value.forEach((n) => {
      if (!n.readAt) {
        n.readAt = readAt;
      }
    });
    unreadCount.value = 0;
    try {
      await service.markAllAsRead();
    } catch (e) {
      console.error("Failed to mark notifications as read", e);
    }
  };

  /**
   * Loads the page below the oldest notification on screen. Cursor-based, so notifications
   * arriving meanwhile cannot make a row repeat or go missing.
   */
  const loadMore = async () => {
    const oldest = notifications.value[notifications.value.length - 1];
    if (!oldest || loadingMore.value) {
      return;
    }
    loadingMore.value = true;
    try {
      const page = await service.list(oldest.id);
      notifications.value = mergeById(notifications.value, page.notifications);
      hasMore.value = page.hasMore;
      expanded = true;
    } catch (e) {
      console.error("Failed to load older notifications", e);
    } finally {
      loadingMore.value = false;
    }
  };

  /** Must run on logout, or one user's notifications outlive their session in the tab. */
  const reset = () => {
    notifications.value = [];
    unreadCount.value = 0;
    hasMore.value = false;
    expanded = false;
    consecutiveFailures = 0;
  };

  return {
    notifications,
    unreadCount,
    loading,
    loadingMore,
    hasMore,
    connected,
    fetchAll,
    loadMore,
    connect,
    disconnect,
    markAsRead,
    markAllAsRead,
    reset,
  };
}
